import { useEffect, useReducer, useRef } from "react";

import type { ViewModel } from "@lib/viewModel";

import { useKoObservableOrRawValue } from "@services/useKoObservableOrRawValue";
import { useViewModelObject } from "@services/useViewModelObject";

import type { ExtractObservedType, ExtractSubTypeSetter } from "./types";

/**
 * Access a view model
 *
 * This hook returns a [value, setValue] pair like useState does. For non-observable children of
 * the view model, the setter will throw if called, and is typed as `() => never`
 *
 * @example
 * const [sparseSegmenter] = useViewModel("sparseSegmenter");
 */
export function useViewModel<Model extends keyof ViewModel, Field extends ViewModel[Model] = ViewModel[Model]>(
  field: Model
): [ExtractObservedType<Field>, ExtractSubTypeSetter<Field>] {
  const viewModel = useViewModelObject();
  const subObservable = viewModel[field];
  const [, forceUpdate] = useReducer(() => ({}), {});
  const mounted = useRef(true);

  useEffect(() => {
    if (!viewModel[field]) {
      // because we have no way of knowing when the child observable will exist for sub-fields of the view model,
      // we need to check continuously and force an update when it does exist
      const checkChild = () => {
        if (viewModel[field]) {
          if (mounted.current) {
            forceUpdate();
          }
        } else {
          requestAnimationFrame(checkChild);
        }
      };
      requestAnimationFrame(checkChild);
    }
    return () => {
      mounted.current = false;
    };
  }, [field, viewModel]);

  return useKoObservableOrRawValue(subObservable) as unknown as [
    ExtractObservedType<Field>,
    ExtractSubTypeSetter<Field>,
  ];
}
